/**
 * @file DeliveryTariffList.tsx
 * @description Результаты расчёта доставки: тарифы + предупреждения
 * (phase19.12 шаг 5).
 *
 * Сортировка — по цене для клиента: менеджер сравнивает то, что назовёт
 * клиенту, а не внутреннюю цену перевозчика.
 */

import { useMemo, useState } from "react";

import { DeliveryTariffCard } from "./DeliveryTariffCard";
import { Alert } from "../ui/alert";
import type { DeliveryQuoteOption, DeliveryQuoteResult } from "../../lib/api-shop-carriers";
import { isToDoorTariff } from "../../lib/delivery-quote-meta";

type Segment = "ALL" | "PVZ" | "DOOR";

const SEGMENTS: Array<{ key: Segment; label: string }> = [
  { key: "ALL", label: "Все" },
  { key: "PVZ", label: "До ПВЗ" },
  { key: "DOOR", label: "До двери" },
];

interface Props {
  quote: DeliveryQuoteResult;
  selected: DeliveryQuoteOption | null;
  onSelect: (option: DeliveryQuoteOption) => void;
  clientTariffCode?: number | null;
  clientPrice?: number | null;
  disabled?: boolean;
}

export function DeliveryTariffList({
  quote,
  selected,
  onSelect,
  clientTariffCode,
  clientPrice,
  disabled,
}: Props) {
  const [segment, setSegment] = useState<Segment>("ALL");

  const options = useMemo(() => {
    const sorted = [...quote.options].sort((a, b) => a.price - b.price);
    if (segment === "ALL") return sorted;
    const wantDoor = segment === "DOOR";
    return sorted.filter((o) => isToDoorTariff(o) === wantDoor);
  }, [quote.options, segment]);

  const clientTariffGone =
    clientTariffCode !== null &&
    clientTariffCode !== undefined &&
    quote.options.length > 0 &&
    !quote.options.some((o) => o.tariffCode === clientTariffCode);

  return (
    <div className="flex flex-col gap-3">
      {quote.mock ? (
        <Alert severity="warning" title="Тестовый режим">
          Ключи СДЭК не подключены — тарифы фиктивные. Не называйте их клиенту.
        </Alert>
      ) : null}
      {quote.sandbox ? (
        <Alert severity="warning" title="Тестовый контур СДЭК">
          Расчёт выполнен в среде разработки (edu). Цены не боевые.
        </Alert>
      ) : null}
      {quote.warnings.length > 0 ? (
        <Alert severity="warning" title="Обратите внимание">
          <ul className="list-inside list-disc">
            {quote.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {clientTariffGone ? (
        <Alert severity="warning" title="Тарифа клиента больше нет">
          Тариф {clientTariffCode}, выбранный клиентом, СДЭК больше не предлагает. Выберите
          замену и согласуйте её с клиентом.
        </Alert>
      ) : null}

      <div role="tablist" aria-label="Фильтр тарифов" className="flex shrink-0 gap-1.5">
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={segment === s.key}
            onClick={() => setSegment(s.key)}
            className={`h-9 shrink-0 rounded-full px-4 text-sm transition-colors ${
              segment === s.key
                ? "bg-primary font-medium text-on-primary"
                : "bg-surface-low text-on-surface-variant hover:bg-accent"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {options.length === 0 ? (
        <p className="py-4 text-center text-sm text-on-surface-variant">
          В этой группе тарифов нет — посмотрите остальные.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <DeliveryTariffCard
              key={option.tariffCode}
              option={option}
              selected={selected?.tariffCode === option.tariffCode}
              onSelect={() => onSelect(option)}
              clientTariffCode={clientTariffCode}
              clientPrice={clientPrice}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}
